import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Archive,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Eye,
  FileImage,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError, absolutePhotoUrl } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
  StatusBadge,
  shortDate,
  weekdayFromDateOnly,
} from "@/components/admin/uiKit";
import ArchiveJobDialog from "@/components/admin/ArchiveJobDialog";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "scheduled", label: "Scheduled" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "skipped", label: "Skipped" },
  // R6B — Archived tab. Backed by GET /api/admin/jobs?status=archived.
  { id: "archived", label: "Archived" },
];

/**
 * R6B — read-only list rendered when the Archived tab is active.
 * Displays the 6 columns required by the release directive:
 *   customer, scheduled date, archive_reason, archived_by,
 *   archived_at, status_before_archive.
 * No mutation controls of any kind.
 */
function ArchivedJobsList({ items }) {
  return (
    <Panel className="overflow-hidden" data-testid="admin-jobs-archived-list">
      <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        <div className="md:col-span-3">Customer</div>
        <div className="md:col-span-2">Scheduled</div>
        <div className="md:col-span-3">Reason</div>
        <div className="md:col-span-2">Archived by · at</div>
        <div className="md:col-span-2">Prior status</div>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((j) => (
          <li
            key={j.id}
            className="px-6 py-4 md:grid md:grid-cols-12 md:gap-4 md:items-center"
            data-testid={`admin-jobs-archived-row-${j.id}`}
          >
            <div className="md:col-span-3 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">
                {j.customer_name || j.customer_email}
              </p>
              <p className="text-xs text-slate-500 truncate">{j.customer_email}</p>
            </div>
            <div className="md:col-span-2 text-sm text-slate-700">
              {shortDate(j.scheduled_for)}
            </div>
            <div className="md:col-span-3 text-sm text-slate-700 break-words">
              {j.archive_reason || "—"}
            </div>
            <div className="md:col-span-2 text-xs text-slate-600">
              <p className="font-mono">{j.archived_by || "—"}</p>
              <p className="text-slate-500">
                {j.archived_at ? shortDate(j.archived_at) : "—"}
              </p>
            </div>
            <div className="md:col-span-2">
              <StatusBadge status={j.status_before_archive || "scheduled"} />
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export default function AdminJobs() {
  const [params] = useSearchParams();
  const customerFilter = params.get("customer_id");
  const focusId = params.get("focus");
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  // R6B — archive dialog target. Immutable job snapshot captured on
  // open. Setting to null closes the dialog.
  const [archiveTarget, setArchiveTarget] = useState(null);
  // Sort state — default Date / desc (newest first on initial load).
  // Persists across filter-tab switches; the column set is identical across tabs.
  const [sortBy, setSortBy] = useState("scheduled_for");
  const [sortDir, setSortDir] = useState("desc");

  const load = useMemo(
    () => () => {
      const q = {};
      if (filter !== "all") q.status = filter;
      if (customerFilter) q.customer_id = customerFilter;
      adminApi
        .get("/admin/jobs", { params: q })
        .then(({ data }) => setItems(data.items || []))
        .catch((err) => toast.error(formatAdminError(err)));
    },
    [filter, customerFilter],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (focusId && items?.length) {
      const found = items.find((j) => j.id === focusId);
      if (found) setSelected(found);
    }
  }, [focusId, items]);

  const onSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      // Date defaults to desc when re-selected from another column.
      setSortDir(col === "scheduled_for" ? "desc" : "asc");
    }
  };

  const sortedItems = useMemo(() => {
    if (!items) return null;
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      let av, bv;
      if (sortBy === "scheduled_for") {
        av = a.scheduled_for || "";
        bv = b.scheduled_for || "";
        // Missing dates sort to the oldest end (i.e., bottom in desc, top in asc reverse).
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
      }
      if (sortBy === "customer_name") {
        av = (a.customer_name || a.customer_email || "").toLowerCase();
        bv = (b.customer_name || b.customer_email || "").toLowerCase();
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
      }
      if (sortBy === "photos") {
        av = (a.photos && a.photos.length) || 0;
        bv = (b.photos && b.photos.length) || 0;
        return (av - bv) * dir;
      }
      if (sortBy === "status") {
        av = (a.status || "").toLowerCase();
        bv = (b.status || "").toLowerCase();
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
      }
      return 0;
    };
    return [...items].sort(cmp);
  }, [items, sortBy, sortDir]);

  return (
    <div data-testid="admin-jobs">
      <PageHeader
        overline="Operations"
        title="Jobs"
        description={
          customerFilter
            ? "Showing jobs for one customer. Clear the filter to see everyone."
            : "Mark jobs complete, upload photos, and trigger photo-proof emails."
        }
        actions={
          <GhostButton onClick={load} data-testid="admin-jobs-refresh">
            <RefreshCw size={14} /> Refresh
          </GhostButton>
        }
      />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-jobs-filters">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setFilter(s.id)}
            className={`text-xs font-bold uppercase tracking-[0.18em] px-3 py-2 rounded-lg border transition-colors ${
              filter === s.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`admin-jobs-filter-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : items.length === 0 ? (
        <EmptyState
          title="No jobs found"
          description={
            filter === "archived"
              ? "No archived jobs. Archived jobs from cancellations or manual archives appear here."
              : "Try a different filter, or wait — new customer signups auto-create their next 4 cleanings."
          }
        />
      ) : filter === "archived" ? (
        <ArchivedJobsList items={sortedItems} data-testid="admin-jobs-archived-list" />
      ) : (
        <Panel className="overflow-hidden">
          {/* Mobile sort control — visible only on small screens (matches
              the existing card-stack pattern below). */}
          <MobileSortControl
            sortBy={sortBy}
            sortDir={sortDir}
            onSortByChange={(col) => {
              if (col === sortBy) return;
              setSortBy(col);
              setSortDir(col === "scheduled_for" ? "desc" : "asc");
            }}
            onDirToggle={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          />
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <SortHeader
              className="col-span-3"
              label="Date"
              col="scheduled_for"
              sortBy={sortBy}
              sortDir={sortDir}
              onClick={() => onSort("scheduled_for")}
            />
            <SortHeader
              className="col-span-4"
              label="Customer"
              col="customer_name"
              sortBy={sortBy}
              sortDir={sortDir}
              onClick={() => onSort("customer_name")}
            />
            <SortHeader
              className="col-span-2"
              label="Photos"
              col="photos"
              sortBy={sortBy}
              sortDir={sortDir}
              onClick={() => onSort("photos")}
            />
            <SortHeader
              className="col-span-2"
              label="Status"
              col="status"
              sortBy={sortBy}
              sortDir={sortDir}
              onClick={() => onSort("status")}
            />
            <div className="col-span-1 text-right">—</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {sortedItems.map((j) => (
              <li
                key={j.id}
                className="px-4 sm:px-5 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => setSelected(j)}
                data-testid={`admin-job-row-${j.id}`}
              >
                {/* Mobile card */}
                <div className="md:hidden">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900">
                        {shortDate(j.scheduled_for)}
                      </p>
                      <p className="text-xs text-slate-500">{weekdayFromDateOnly(j.scheduled_for) || j.service_day}</p>
                    </div>
                    <StatusBadge status={j.status} />
                  </div>
                  <div className="mb-3 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-0.5">
                      Customer
                    </p>
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {j.customer_name || j.customer_email}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {j.customer_email}
                    </p>
                  </div>
                  <p className="text-sm text-slate-700 inline-flex items-center gap-1.5 mb-3">
                    <Camera size={14} className="text-slate-400" />
                    {j.photos?.length || 0} photo{(j.photos?.length || 0) === 1 ? "" : "s"}
                  </p>
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center min-h-[44px] rounded-lg bg-slate-900 text-white text-sm font-bold tracking-wide"
                    data-testid={`admin-job-open-${j.id}`}
                  >
                    Open job
                  </button>
                </div>
                {/* Desktop row */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 md:items-center">
                  <div className="md:col-span-3">
                    <p className="font-bold text-slate-900">
                      {shortDate(j.scheduled_for)}
                    </p>
                    <p className="text-xs text-slate-500">{weekdayFromDateOnly(j.scheduled_for) || j.service_day}</p>
                  </div>
                  <div className="md:col-span-4 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {j.customer_name || j.customer_email}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {j.customer_email}
                    </p>
                  </div>
                  <div className="md:col-span-2 text-sm text-slate-600 inline-flex items-center gap-1.5">
                    <Camera size={14} className="text-slate-400" />
                    {j.photos?.length || 0}
                  </div>
                  <div className="md:col-span-2">
                    <StatusBadge status={j.status} />
                  </div>
                  <div className="md:col-span-1 md:text-right text-xs font-bold text-sky-700 flex md:justify-end items-center gap-2">
                    {j.status !== "archived" && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setArchiveTarget(j);
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-600 hover:border-slate-300 hover:text-slate-900"
                        title="Archive this job"
                        data-testid={`admin-job-archive-btn-${j.id}`}
                      >
                        <Archive size={12} />
                        Archive
                      </button>
                    )}
                    <span>Open →</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {selected && (
        <JobDrawer
          jobId={selected.id}
          onClose={() => setSelected(null)}
          onChanged={load}
          onArchive={(jobDoc) => setArchiveTarget(jobDoc)}
        />
      )}

      {archiveTarget && (
        <ArchiveJobDialog
          job={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onArchived={() => {
            // R6B — refetch the currently mounted Jobs data only.
            // No cross-page coupling to an unmounted Overview page.
            load();
            // If the drawer was open on the same job, close it — the
            // job is now archived and its drawer would show read-only
            // audit anyway; letting the user re-open from the row
            // keeps the flow explicit.
            if (selected && selected.id === archiveTarget.id) {
              setSelected(null);
            }
          }}
        />
      )}
    </div>
  );
}

function SortHeader({ className, label, col, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} text-left inline-flex items-center gap-1 uppercase tracking-[0.22em] text-[10px] font-bold transition-colors ${
        active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
      }`}
      data-testid={`admin-jobs-sort-${col}`}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <Icon
        size={12}
        className={active ? "text-sky-500" : "text-slate-300"}
        aria-hidden="true"
      />
    </button>
  );
}

const MOBILE_SORT_OPTIONS = [
  { col: "scheduled_for", label: "Date" },
  { col: "customer_name", label: "Customer" },
  { col: "photos", label: "Photos" },
  { col: "status", label: "Status" },
];

function MobileSortControl({ sortBy, sortDir, onSortByChange, onDirToggle }) {
  return (
    <div
      className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/60"
      data-testid="admin-jobs-mobile-sort"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 shrink-0">Sort</span>
      <select
        value={sortBy}
        onChange={(e) => onSortByChange(e.target.value)}
        className="flex-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-800 focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
        data-testid="admin-jobs-mobile-sort-select"
      >
        {MOBILE_SORT_OPTIONS.map(({ col, label }) => (
          <option key={col} value={col}>{label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={onDirToggle}
        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-700 inline-flex items-center gap-1 text-xs font-bold"
        aria-label={sortDir === "asc" ? "Sort ascending — tap to reverse" : "Sort descending — tap to reverse"}
        data-testid="admin-jobs-mobile-sort-dir"
      >
        {sortDir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {sortDir === "asc" ? "ASC" : "DESC"}
      </button>
    </div>
  );
}

function JobDrawer({ jobId, onClose, onChanged, onArchive }) {
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = () => {
    adminApi
      .get(`/admin/jobs/${jobId}`)
      .then(({ data }) => {
        setJob(data.job);
        setNotes(data.job.notes || "");
      })
      .catch((err) => toast.error(formatAdminError(err)));
  };

  useEffect(() => {
    load();
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await adminApi.post(`/admin/jobs/${jobId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} uploaded`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatAdminError(err, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await adminApi.delete(`/admin/jobs/${jobId}/photos/${photoId}`);
      toast.success("Photo removed");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await adminApi.patch(`/admin/jobs/${jobId}`, { notes });
      toast.success("Notes saved");
      onChanged?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSavingNotes(false);
    }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await adminApi.post(`/admin/jobs/${jobId}/complete`);
      toast.success("Marked completed", {
        description: data.email_sent_to
          ? `Email queued to ${data.email_sent_to}`
          : "No customer email on file.",
      });
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      data-testid="admin-job-drawer"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[600px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
              Job
            </p>
            <p className="text-sm text-slate-700 font-bold">
              {job ? shortDate(job.scheduled_for) : "Loading…"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
            data-testid="admin-job-drawer-close"
          >
            <X size={16} />
          </button>
        </header>

        {!job ? (
          <div className="p-10 text-center">
            <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-7">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2
                  className="text-2xl font-extrabold tracking-tight text-slate-900"
                  style={{ fontFamily: "Outfit, ui-sans-serif" }}
                >
                  {job.customer_name || job.customer_email}
                </h2>
                <p className="text-sm text-slate-500">{job.customer_email}</p>
              </div>
              <StatusBadge status={job.status} />
            </div>

            <Panel className="p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Photos ({job.photos?.length || 0})
                </p>
                {job.status !== "archived" && (
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-2 transition-colors">
                    <Upload size={12} />
                    {uploading ? "Uploading…" : "Add photos"}
                    <input
                      ref={fileInput}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onUpload}
                      className="hidden"
                      data-testid="admin-job-photo-input"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>

              {(!job.photos || job.photos.length === 0) ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                  <FileImage size={20} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">
                    {job.status === "archived"
                      ? "No photos on this archived job."
                      : "No photos yet. Upload before marking complete to email proof to the customer."}
                  </p>
                </div>
              ) : (
                <ul className="grid grid-cols-2 gap-3">
                  {job.photos.map((p) => (
                    <li
                      key={p.id}
                      className="relative group rounded-lg overflow-hidden bg-slate-100 aspect-[4/3]"
                      data-testid={`admin-job-photo-${p.id}`}
                    >
                      <img
                        src={absolutePhotoUrl(p.url)}
                        alt="Cleaned bin proof"
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                      {job.status !== "archived" && (
                        <button
                          type="button"
                          onClick={() => removePhoto(p.id)}
                          className="absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/90 text-rose-600 hover:bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove"
                          data-testid={`admin-job-photo-remove-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <a
                        href={absolutePhotoUrl(p.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-white/90 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Eye size={10} /> View
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {job.status === "archived" ? (
              <Panel
                className="p-5 border-slate-300 bg-slate-50"
                data-testid="admin-job-drawer-archived-panel"
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-600 font-bold mb-3">
                  Archived — read only
                </p>
                <dl className="space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-slate-500 font-semibold text-xs uppercase tracking-[0.14em]">Reason</dt>
                    <dd className="text-slate-900 text-right break-words max-w-[70%]">
                      {job.archive_reason || "—"}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-slate-500 font-semibold text-xs uppercase tracking-[0.14em]">By</dt>
                    <dd className="text-slate-900 font-mono text-xs">{job.archived_by || "—"}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-slate-500 font-semibold text-xs uppercase tracking-[0.14em]">At</dt>
                    <dd className="text-slate-900 text-xs">
                      {job.archived_at ? shortDate(job.archived_at) : "—"}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-slate-500 font-semibold text-xs uppercase tracking-[0.14em]">Prior status</dt>
                    <dd>
                      <StatusBadge status={job.status_before_archive || "scheduled"} />
                    </dd>
                  </div>
                </dl>
                <p className="mt-4 text-xs text-slate-500">
                  R6 does not provide an unarchive path. This job is preserved for audit only.
                </p>
              </Panel>
            ) : (
              <>
                <Panel className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
                    Note for customer
                  </p>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional. Shown in the photo-proof email."
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                    data-testid="admin-job-notes"
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <GhostButton onClick={saveNotes} disabled={savingNotes}>
                      {savingNotes ? "Saving…" : "Save note"}
                    </GhostButton>
                  </div>
                </Panel>

                {job.status !== "completed" && (
                  <Panel className="p-5 border-emerald-200 bg-emerald-50/50">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold mb-2">
                      Mark complete
                    </p>
                    <p className="text-sm text-emerald-900 mb-4">
                      This will set the job to <strong>completed</strong> and queue a
                      photo-proof email to the customer.
                    </p>
                    <PrimaryButton
                      onClick={complete}
                      disabled={completing}
                      data-testid="admin-job-complete"
                    >
                      <CheckCircle2 size={14} />
                      {completing ? "Sending…" : "Mark Job Completed & Send Email"}
                    </PrimaryButton>
                  </Panel>
                )}

                {job.status === "completed" && (
                  <Panel className="p-5 border-emerald-200 bg-emerald-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 size={16} className="text-emerald-700" />
                      <p className="font-bold text-emerald-900">Completed</p>
                    </div>
                    {job.completed_at && (
                      <p className="text-sm text-emerald-800">
                        Marked {shortDate(job.completed_at)}.
                      </p>
                    )}
                    {job.service_completed_email_sent_at ? (
                      <p
                        className="text-sm text-emerald-800 mt-1"
                        data-testid="admin-job-email-already-sent"
                      >
                        Email already sent on {shortDate(job.service_completed_email_sent_at)}
                        {job.service_completed_email_to ? (
                          <> to <span className="font-semibold">{job.service_completed_email_to}</span></>
                        ) : null}
                        .
                      </p>
                    ) : (
                      <p className="text-sm text-emerald-800 mt-1">
                        Photo-proof email logged.
                      </p>
                    )}
                  </Panel>
                )}

                <div>
                  <GhostButton
                    onClick={() => onArchive?.(job)}
                    data-testid="admin-job-drawer-archive"
                  >
                    <Archive size={12} />
                    Archive this job…
                  </GhostButton>
                </div>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
